import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Clock, Users, CheckCircle2, PhoneCall } from "lucide-react";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import SiteImage from "@/components/SiteImage";

const HERO_IMG = "https://images.unsplash.com/photo-1758691462878-6edc3d3da1be?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const BENEFIT_IMG = "https://images.unsplash.com/photo-1758691461935-202e2ef6b69f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const STEPS = [
  { icon: "MousePointerClick", title: "Vous exprimez votre besoin", text: "Décrivez la mission ou le projet, avec ou sans compte." },
  { icon: "CalendarCheck", title: "Nous vérifions les disponibilités", text: "Notre équipe confirme la capacité et vous propose une solution." },
  { icon: "Headset", title: "Nous prenons le relais", text: "Une professionnelle dédiée gère votre secrétariat à distance." },
];
const ADV = [
  { icon: "Clock", title: "Flexibilité totale", text: "Quelques heures, une journée, ou un besoin récurrent." },
  { icon: "ShieldCheck", title: "Discrétion suisse", text: "Confidentialité et rigueur, conformément à vos exigences." },
  { icon: "Wallet", title: "Sans recrutement", text: "Pas de charges fixes ni de gestion RH interne." },
  { icon: "Sparkles", title: "Expertise médicale", text: "Des secrétaires formées à l'environnement médical." },
];

function Section({ id, children, className = "" }) {
  return <section id={id} className={`py-16 md:py-24 ${className}`}>{children}</section>;
}
const Container = ({ children, className = "" }) => <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>;
const Overline = ({ children }) => <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{children}</span>;

export default function Home() {
  const [content, setContent] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [services, setServices] = useState([]);
  const [faq, setFaq] = useState([]);
  const [team, setTeam] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/website/content").then(({ data }) => setContent(data || {})).finally(() => setLoaded(true));
    api.get("/services").then(({ data }) => setServices(data || []));
    api.get("/website/faq").then(({ data }) => setFaq(data || []));
    api.get("/website/team").then(({ data }) => setTeam(data || []));
  }, []);

  const c = content;
  const hero = c.hero || {};
  const featured = services.filter((s) => s.featured).slice(0, 6);

  return (
    <div>
      {/* HERO */}
      <Section className="pt-16 md:pt-20 pb-10 md:pb-16">
        <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <Overline>{hero.overline || "Secrétariat médical suisse à distance"}</Overline>
            <h1 className="mt-4 font-heading font-bold tracking-tight text-slate-900 leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              {hero.title || "Votre secrétariat médical, flexible et à distance."}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">{hero.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="bg-primary hover:opacity-95" onClick={() => navigate("/services")} data-testid="hero-services-btn">
                {hero.cta_primary || "Découvrir nos services"}
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/demande")} data-testid="hero-request-btn">
                {hero.cta_secondary || "Faire une demande"} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button size="lg" variant="ghost" onClick={() => navigate("/disponibilites")} data-testid="hero-availability-btn">
                {hero.cta_tertiary || "Voir les disponibilités"}
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Sans engagement</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Demande sans compte</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Toute la Suisse</span>
            </div>
          </div>
          <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-200">
              <SiteImage loading={!loaded} mediaId={content.images?.hero} fallback={HERO_IMG} alt="Cabinet médical suisse"
                className="w-full h-[360px] md:h-[460px]"
                posX={content.images_pos?.hero?.x ?? 50} posY={content.images_pos?.hero?.y ?? 50} fit={content.images_pos?.hero?.fit || "cover"} zoom={content.images_pos?.hero?.zoom ?? 1} />
            </div>
            <div className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-xl shadow-xl border border-slate-100 p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><PhoneCall className="h-5 w-5 text-primary" /></div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Appels & rendez-vous</p>
                <p className="text-xs text-slate-500">Gérés à votre place</p>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* SERVICES */}
      <Section id="services" className="bg-white border-y border-border">
        <Container>
          <div className="max-w-2xl"><Overline>Nos services</Overline>
            <h2 className="mt-3 font-heading font-semibold tracking-tight text-slate-900 text-3xl lg:text-4xl">Un secrétariat complet, à la carte</h2>
            <p className="mt-4 text-slate-600 text-lg">De la gestion des appels à l'accompagnement de projets, choisissez ce dont votre cabinet a besoin.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((s) => (
              <Link key={s.service_id} to="/services" data-testid={`home-service-${s.service_id}`}
                className="group bg-white rounded-xl border border-slate-200/90 shadow-sm hover:shadow-md hover:border-primary/30 transition-all p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon name={s.icon} className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                </div>
                <h3 className="mt-5 font-heading font-semibold text-lg text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed line-clamp-3">{s.description}</p>
                <span className="mt-4 inline-flex items-center text-sm font-medium text-primary">En savoir plus <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
              </Link>
            ))}
          </div>
          <div className="mt-10"><Button variant="outline" size="lg" onClick={() => navigate("/services")}>Voir tous les services</Button></div>
        </Container>
      </Section>

      {/* HOW */}
      {c.how?.visible !== false && (
        <Section>
          <Container>
            <div className="text-center max-w-2xl mx-auto"><Overline>Comment ça marche</Overline>
              <h2 className="mt-3 font-heading font-semibold tracking-tight text-slate-900 text-3xl lg:text-4xl">{c.how?.title || "Comment ça marche"}</h2>
              <p className="mt-4 text-slate-600 text-lg">{c.how?.subtitle}</p>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
              {STEPS.map((s, i) => (
                <div key={i} className="relative bg-white rounded-xl border border-slate-200 p-8">
                  <span className="absolute -top-4 left-8 h-8 w-8 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center font-mono-metric">{i + 1}</span>
                  <div className="h-11 w-11 rounded-lg bg-accent/10 flex items-center justify-center mb-4"><Icon name={s.icon} className="h-5 w-5 text-accent" /></div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {/* ADVANTAGES / WHY */}
      <Section className="bg-[hsl(var(--dark-section-bg))] text-[hsl(var(--dark-section-text))]">
        <Container className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Pourquoi externaliser</span>
            <h2 className="mt-3 font-heading font-semibold tracking-tight text-3xl lg:text-4xl">{c.advantages?.title || "Pourquoi externaliser votre secrétariat"}</h2>
            <p className="mt-4 text-[hsl(var(--dark-section-muted))] text-lg">{c.advantages?.subtitle}</p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {ADV.map((a, i) => (
                <div key={i} className="flex gap-3">
                  <div className="h-10 w-10 shrink-0 rounded-lg bg-white/10 flex items-center justify-center"><Icon name={a.icon} className="h-5 w-5 text-accent" /></div>
                  <div><h4 className="font-semibold">{a.title}</h4><p className="mt-1 text-sm text-[hsl(var(--dark-section-muted))] leading-relaxed">{a.text}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-2xl">
            <SiteImage loading={!loaded} mediaId={content.images?.benefit} fallback={BENEFIT_IMG} alt="Médecin concentré sur ses patients"
              className="w-full h-[420px]"
              posX={content.images_pos?.benefit?.x ?? 50} posY={content.images_pos?.benefit?.y ?? 50} fit={content.images_pos?.benefit?.fit || "cover"} zoom={content.images_pos?.benefit?.zoom ?? 1} />
          </div>
        </Container>
      </Section>

      {/* CABINETS / PROJECTS cards */}
      <Section className="bg-white border-y border-border">
        <Container className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { to: "/pour-les-cabinets", icon: "Building2", t: c.practices?.title || "Solutions pour cabinets", d: c.practices?.subtitle },
            { to: "/ouverture-reprise-cabinet", icon: "KeyRound", t: c.opening?.title || "Ouverture & reprise", d: c.opening?.subtitle },
            { to: "/office-manager", icon: "Briefcase", t: c.office?.title || "Office Manager externalisé", d: c.office?.subtitle },
          ].map((x) => (
            <Link key={x.to} to={x.to} className="group rounded-xl border border-slate-200 p-8 hover:border-primary/40 hover:shadow-md transition-all bg-gradient-to-b from-white to-slate-50/50">
              <Icon name={x.icon} className="h-8 w-8 text-primary" />
              <h3 className="mt-5 font-heading font-semibold text-xl text-slate-900">{x.t}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{x.d}</p>
              <span className="mt-4 inline-flex items-center text-sm font-medium text-primary">Découvrir <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
            </Link>
          ))}
        </Container>
      </Section>

      {/* TEAM */}
      {team.length > 0 && c.team?.visible !== false && (
        <Section>
          <Container>
            <div className="max-w-2xl"><Overline>Notre équipe</Overline>
              <h2 className="mt-3 font-heading font-semibold tracking-tight text-slate-900 text-3xl lg:text-4xl">{c.team?.title || "Notre équipe"}</h2>
              <p className="mt-4 text-slate-600 text-lg">{c.team?.subtitle}</p>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {team.map((m) => (
                <div key={m.user_id} className="bg-white rounded-xl border border-slate-200 p-6 flex items-start gap-4">
                  <Avatar className="h-16 w-16"><AvatarImage src={m.photo} /><AvatarFallback className="bg-primary/10 text-primary font-semibold">{(m.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                  <div><h3 className="font-heading font-semibold text-slate-900">{m.name}</h3><p className="text-sm text-primary font-medium">{m.job_title}</p><p className="mt-2 text-sm text-slate-500 leading-relaxed">{m.bio}</p></div>
                </div>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <Section className="bg-white border-t border-border">
          <Container className="max-w-3xl">
            <div className="text-center"><Overline>FAQ</Overline>
              <h2 className="mt-3 font-heading font-semibold tracking-tight text-slate-900 text-3xl lg:text-4xl">{c.faq?.title || "Questions fréquentes"}</h2>
            </div>
            <Accordion type="single" collapsible className="mt-10">
              {faq.map((f) => (
                <AccordionItem key={f.faq_id} value={f.faq_id} data-testid={`faq-${f.faq_id}`}>
                  <AccordionTrigger className="text-left font-medium text-slate-900">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Container>
        </Section>
      )}

      {/* CONTACT CTA */}
      <Section>
        <Container>
          <div className="rounded-2xl bg-primary text-white p-10 md:p-16 text-center relative overflow-hidden">
            <div className="relative">
              <h2 className="font-heading font-semibold tracking-tight text-3xl lg:text-4xl">{c.contact?.title || "Parlons de votre cabinet"}</h2>
              <p className="mt-4 text-white/80 text-lg max-w-xl mx-auto">{c.contact?.subtitle}</p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Button size="lg" variant="secondary" onClick={() => navigate("/demande")} data-testid="cta-final-request">{c.contact?.cta || "Faire une demande"}</Button>
                <Button size="lg" variant="outline" className="bg-transparent text-white border-white/40 hover:bg-white/10" onClick={() => navigate("/contact")}>Nous contacter</Button>
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
